import React, { useEffect, useMemo, useState } from "react";
import { getItinerary, getRoutes, IItinerary } from "./Itinerary.service";
import './Itinerary.style.scss'
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Card } from "primereact/card";

interface RouteOption {
    ID_RUTA: number;
    NOMBRE: string;
    ONLY_NAME: string;
    TIPO: string;
}

const hasValue = (value?: string) => !!value && value.trim() !== '';

const Itinerary = () => {
    const [itinerary, setItinerary] = useState<IItinerary>({name: '', stroke: []});
    const [routes, setRoutes] = useState<RouteOption[]>([]);
    const [selectedRoute, setSelectedRoute] = useState<RouteOption | null>(null);

    useEffect(() => {
        getRoutes()
            .then(setRoutes)
            .catch(error => console.error(error));
    }, []);

    // Mostramos Turno 2 / Turno 3 si al menos una parada tiene ese horario; se ocultan solo si ninguna lo tiene.
    const showTurno2 = useMemo(
        () => itinerary.stroke.some(s => hasValue(s.time2)),
        [itinerary.stroke]
    );
    const showTurno3 = useMemo(
        () => itinerary.stroke.some(s => hasValue(s.time3)),
        [itinerary.stroke]
    );

    const downloadExcel = async () => {
        try {
            const ExcelJS = (await import('exceljs')).default;

            // Paleta alineada con la app
            const DARK = 'FF1C1C1C';
            const CHOCO = 'FFD2691E';
            const ZEBRA = 'FFFAF6F2';
            const LINE = 'FFEDEDED';
            const WHITE = 'FFFFFFFF';

            const headerLabels = ['Parada', 'Turno 1'];
            if (showTurno2) headerLabels.push('Turno 2');
            if (showTurno3) headerLabels.push('Turno 3');
            headerLabels.push('Referencia');
            const colCount = headerLabels.length;

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'SUMA';
            workbook.created = new Date();

            const sheet = workbook.addWorksheet('Itinerario', {
                views: [{ state: 'frozen', ySplit: 4 }],
            });

            const nameWidth = itinerary.stroke.reduce((w, s) => Math.max(w, s.name.length + 4), 28);
            const commentWidth = itinerary.stroke.reduce((w, s) => Math.max(w, (s.comments?.length ?? 0) + 4), 24);
            const widths = [nameWidth, ...headerLabels.slice(1, -1).map(() => 14), commentWidth];
            sheet.columns = widths.map(width => ({ width }));

            // Título: nombre de la ruta
            sheet.mergeCells(1, 1, 1, colCount);
            const titleCell = sheet.getCell(1, 1);
            titleCell.value = itinerary.name.toUpperCase();
            titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: CHOCO } };
            titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
            titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } };
            sheet.getRow(1).height = 32;

            // Subtítulo: descripción + fecha
            sheet.mergeCells(2, 1, 2, colCount);
            const subCell = sheet.getCell(2, 1);
            const today = new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
            subCell.value = `Itinerario de paradas  ·  Generado el ${today}`;
            subCell.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF888888' } };
            subCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
            sheet.getRow(2).height = 18;

            sheet.getRow(3).height = 6; // separador

            // Encabezados de columna
            const headerRow = sheet.getRow(4);
            headerRow.values = headerLabels;
            headerRow.height = 22;
            headerRow.eachCell(cell => {
                cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: WHITE } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CHOCO } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = {
                    top: { style: 'thin', color: { argb: CHOCO } },
                    bottom: { style: 'thin', color: { argb: CHOCO } },
                    left: { style: 'thin', color: { argb: 'FFE8D9CC' } },
                    right: { style: 'thin', color: { argb: 'FFE8D9CC' } },
                };
            });

            // Filas de datos
            itinerary.stroke.forEach((s, i) => {
                const values: string[] = [s.name, s.time1 || '—'];
                if (showTurno2) values.push(s.time2 || '—');
                if (showTurno3) values.push(s.time3 || '—');
                values.push(s.comments || '');

                const row = sheet.addRow(values);
                row.height = 18;
                const zebra = i % 2 === 1;
                row.eachCell((cell, colNumber) => {
                    cell.font = { name: 'Calibri', size: 10, color: { argb: 'FF333333' } };
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: colNumber === 1 || colNumber === colCount ? 'left' : 'center',
                        wrapText: colNumber === colCount,
                        indent: colNumber === 1 || colNumber === colCount ? 1 : 0,
                    };
                    if (zebra) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
                    }
                    cell.border = { bottom: { style: 'thin', color: { argb: LINE } } };
                });
            });

            sheet.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: colCount } };

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer as ArrayBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `ITINERARIO_${itinerary.name}.xlsx`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Error generando el Excel:', error);
        }
    };

    const handleRouteSelect = (e: { value: RouteOption }) => {
        setSelectedRoute(e.value);
        getItinerary(e.value.ID_RUTA, e.value.TIPO)
            .then(setItinerary)
            .catch(error => console.error(error));
    };

    return (
        <div className="itinerary-container">
            <Card className="itinerary-card">
                <div className="itinerary-toolbar">
                    <div className="route-select">
                        <label htmlFor="route-dd">Ruta</label>
                        <Dropdown
                            inputId="route-dd"
                            value={selectedRoute}
                            options={routes}
                            optionLabel="NOMBRE"
                            filter
                            placeholder="Selecciona una ruta"
                            onChange={handleRouteSelect}
                            panelClassName="route-select-panel"
                        />
                    </div>
                    {itinerary.stroke.length > 0 && (
                        <Button
                            label="Descargar Excel"
                            icon="pi pi-file-excel"
                            className="p-button-success p-button-outlined"
                            onClick={downloadExcel}
                        />
                    )}
                </div>

                {itinerary.stroke.length > 0 ? (
                    <>
                        <h2 className="itinerary-route-name">
                            <i className="pi pi-map-marker" /> {itinerary.name}
                        </h2>

                        <div className="itinerary-table-wrap">
                            <table className="itinerary-table">
                                <thead>
                                    <tr>
                                        <th className="itinerary-table__index-col">#</th>
                                        <th className="itinerary-table__name-col">Parada</th>
                                        <th>Turno 1</th>
                                        {showTurno2 && <th>Turno 2</th>}
                                        {showTurno3 && <th>Turno 3</th>}
                                        <th className="itinerary-table__ref-col">Referencia</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {itinerary.stroke.map((stop, index) => (
                                        <tr key={index}>
                                            <td className="itinerary-table__index">{index + 1}</td>
                                            <td className="itinerary-table__name">{stop.name}</td>
                                            <td className="itinerary-table__time">
                                                {hasValue(stop.time1) ? stop.time1 : '—'}
                                            </td>
                                            {showTurno2 && (
                                                <td className="itinerary-table__time">
                                                    {hasValue(stop.time2) ? stop.time2 : '—'}
                                                </td>
                                            )}
                                            {showTurno3 && (
                                                <td className="itinerary-table__time">
                                                    {hasValue(stop.time3) ? stop.time3 : '—'}
                                                </td>
                                            )}
                                            <td className="itinerary-table__ref">
                                                {hasValue(stop.comments) ? stop.comments : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="itinerary-stops">
                            {itinerary.stroke.map((stop, index) => (
                                <div className="stop-card" key={index}>
                                    <div className="stop-card__head">
                                        <span className="stop-card__index">{index + 1}</span>
                                        <div className="stop-card__title">
                                            <h3>{stop.name}</h3>
                                            {hasValue(stop.comments) && <span>{stop.comments}</span>}
                                        </div>
                                    </div>
                                    <div className="stop-card__turnos">
                                        <div className="stop-turno">
                                            <span className="stop-turno__label">Turno 1</span>
                                            <span className="stop-turno__time">
                                                {hasValue(stop.time1) ? stop.time1 : '—'}
                                            </span>
                                        </div>
                                        {showTurno2 && (
                                            <div className="stop-turno">
                                                <span className="stop-turno__label">Turno 2</span>
                                                <span className="stop-turno__time">
                                                    {hasValue(stop.time2) ? stop.time2 : '—'}
                                                </span>
                                            </div>
                                        )}
                                        {showTurno3 && (
                                            <div className="stop-turno">
                                                <span className="stop-turno__label">Turno 3</span>
                                                <span className="stop-turno__time">
                                                    {hasValue(stop.time3) ? stop.time3 : '—'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="itinerary-empty">
                        <i className="pi pi-info-circle" />
                        <span>Selecciona una ruta para poder ver el listado de paradas</span>
                    </div>
                )}
            </Card>
        </div>
    );
}

export default Itinerary;
